import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Clock, Save, AlertCircle, CheckCircle } from 'lucide-react';

// Utility function to format 24-hour time to 12-hour format
const formatTo12Hour = (time24) => {
  if (!time24) return '';
  
  const [hours, minutes] = time24.split(':');
  const hour12 = parseInt(hours);
  const displayHour = hour12 === 0 ? 12 : hour12 > 12 ? hour12 - 12 : hour12;
  const period = hour12 >= 12 ? 'PM' : 'AM';
  
  return `${displayHour}:${minutes} ${period}`;
};

const WorkHoursSettings = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [scheduleMode, setScheduleMode] = useState('simple'); // 'simple' or 'flexible'
  const [workHours, setWorkHours] = useState({
    start: '09:00',
    end: '17:00',
    work_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    timezone: 'America/New_York'
  });
  const [dailySchedule, setDailySchedule] = useState({
    monday: { enabled: true, start: '09:00', end: '17:00' },
    tuesday: { enabled: true, start: '09:00', end: '17:00' },
    wednesday: { enabled: true, start: '09:00', end: '17:00' },
    thursday: { enabled: true, start: '09:00', end: '17:00' },
    friday: { enabled: true, start: '09:00', end: '17:00' },
    saturday: { enabled: false, start: '09:00', end: '17:00' },
    sunday: { enabled: false, start: '09:00', end: '17:00' }
  });

  useEffect(() => {
    fetchWorkHours();
  }, []);

  const fetchWorkHours = async () => {
    try {
      setLoading(true);
      
      const response = await api.get('/users/work-hours');
      
      if (response.data) {
        setWorkHours({
          start: response.data.work_hours_start?.substring(0, 5) || '09:00',
          end: response.data.work_hours_end?.substring(0, 5) || '17:00',
          work_days: response.data.work_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
          timezone: response.data.timezone || 'America/New_York'
        });
        
        // Check if daily_schedule is set
        if (response.data.daily_schedule) {
          setScheduleMode('flexible');
          setDailySchedule(response.data.daily_schedule);
        } else {
          setScheduleMode('simple');
        }
      }
    } catch (error) {
      console.error('Failed to fetch work hours:', error);
      setMessage({ type: 'error', text: 'Failed to load work hours' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      
      const workHoursData = {
        work_hours_start: workHours.start + ':00',
        work_hours_end: workHours.end + ':00',
        work_days: workHours.work_days,
        daily_schedule: scheduleMode === 'flexible' ? dailySchedule : null,
        timezone: workHours.timezone
      };
      
      await api.put('/users/work-hours', workHoursData);
      
      setMessage({ type: 'success', text: 'Work hours updated successfully!' });
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      console.error('Failed to update work hours:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to update work hours' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTimeChange = (field, value) => {
    setWorkHours(prev => ({ ...prev, [field]: value }));
  };

  const toggleWorkDay = (day) => {
    setWorkHours(prev => {
      const workDays = prev.work_days.includes(day)
        ? prev.work_days.filter(d => d !== day)
        : [...prev.work_days, day];
      return { ...prev, work_days: workDays };
    });
  };

  const handleDayScheduleChange = (day, field, value) => {
    setDailySchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  const switchToFlexible = () => {
    // Initialize daily schedule from current simple schedule
    const newDailySchedule = {};
    ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].forEach(day => {
      newDailySchedule[day] = {
        enabled: workHours.work_days.includes(day),
        start: workHours.start,
        end: workHours.end
      };
    });
    setDailySchedule(newDailySchedule);
    setScheduleMode('flexible');
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
        <div className="h-24 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Message Display */}
      {message.text && (
        <div className={`p-4 rounded-lg flex items-center space-x-3 ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Introduction */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-blue-900 mb-1">Configure Your Availability</h3>
            <p className="text-sm text-blue-700">
              Set your working hours and days to help with scheduling and task assignments. This affects when tasks are automatically scheduled for you.
            </p>
          </div>
        </div>
      </div>

      {/* Schedule Mode Toggle */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">Schedule Type</h3>
          <p className="text-xs text-gray-600">Choose how you want to set your work hours</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setScheduleMode('simple')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
              scheduleMode === 'simple'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Same Every Day
          </button>
          <button
            onClick={switchToFlexible}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
              scheduleMode === 'flexible'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Different Per Day
          </button>
        </div>
      </div>

      {/* Work Hours Configuration */}
      <div className="space-y-6">
        {scheduleMode === 'simple' ? (
          <>
            {/* Time Range */}
            <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Working Hours</label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">Start Time</label>
              <input
                type="time"
                value={workHours.start}
                onChange={(e) => handleTimeChange('start', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <div className="text-xs text-gray-500 mt-1">
                {formatTo12Hour(workHours.start)}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">End Time</label>
              <input
                type="time"
                value={workHours.end}
                onChange={(e) => handleTimeChange('end', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <div className="text-xs text-gray-500 mt-1">
                {formatTo12Hour(workHours.end)}
              </div>
            </div>
          </div>
        </div>

        {/* Work Days */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Work Days</label>
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'monday', label: 'Monday' },
              { key: 'tuesday', label: 'Tuesday' },
              { key: 'wednesday', label: 'Wednesday' },
              { key: 'thursday', label: 'Thursday' },
              { key: 'friday', label: 'Friday' },
              { key: 'saturday', label: 'Saturday' },
              { key: 'sunday', label: 'Sunday' }
            ].map(day => (
              <button
                key={day.key}
                type="button"
                onClick={() => toggleWorkDay(day.key)}
                className={`px-4 py-2 text-sm font-medium rounded-lg border transition-all ${
                  workHours.work_days.includes(day.key)
                    ? 'bg-gradient-to-r from-purple-600/20 to-pink-600/20 border-purple-300 text-purple-700 hover:from-purple-600/30 hover:to-pink-600/30'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {day.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Selected: {workHours.work_days.length > 0 
              ? workHours.work_days.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ')
              : 'None'
            }
          </p>
        </div>

          </>
        ) : (
          /* Flexible Schedule - Per Day Configuration */
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700 mb-3">Daily Schedule</label>
            {[
              { key: 'monday', label: 'Monday' },
              { key: 'tuesday', label: 'Tuesday' },
              { key: 'wednesday', label: 'Wednesday' },
              { key: 'thursday', label: 'Thursday' },
              { key: 'friday', label: 'Friday' },
              { key: 'saturday', label: 'Saturday' },
              { key: 'sunday', label: 'Sunday' }
            ].map(day => (
              <div key={day.key} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center min-w-[140px]">
                  <input
                    type="checkbox"
                    checked={dailySchedule[day.key]?.enabled || false}
                    onChange={(e) => handleDayScheduleChange(day.key, 'enabled', e.target.checked)}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 mr-3"
                  />
                  <label className="text-sm font-medium text-gray-900">{day.label}</label>
                </div>
                {dailySchedule[day.key]?.enabled && (
                  <div className="flex items-center gap-3 flex-1">
                    <div className="flex-1">
                      <input
                        type="time"
                        value={dailySchedule[day.key]?.start || '09:00'}
                        onChange={(e) => handleDayScheduleChange(day.key, 'start', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        {formatTo12Hour(dailySchedule[day.key]?.start || '09:00')}
                      </div>
                    </div>
                    <span className="text-gray-500">to</span>
                    <div className="flex-1">
                      <input
                        type="time"
                        value={dailySchedule[day.key]?.end || '17:00'}
                        onChange={(e) => handleDayScheduleChange(day.key, 'end', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        {formatTo12Hour(dailySchedule[day.key]?.end || '17:00')}
                      </div>
                    </div>
                  </div>
                )}
                {!dailySchedule[day.key]?.enabled && (
                  <span className="text-sm text-gray-500 italic">Day off</span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Timezone (shown for both modes) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Timezone</label>
          <select
            value={workHours.timezone}
            onChange={(e) => handleTimeChange('timezone', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="America/New_York">Eastern Time (ET)</option>
            <option value="America/Chicago">Central Time (CT)</option>
            <option value="America/Denver">Mountain Time (MT)</option>
            <option value="America/Los_Angeles">Pacific Time (PT)</option>
            <option value="America/Phoenix">Arizona Time (MST)</option>
            <option value="America/Anchorage">Alaska Time (AKT)</option>
            <option value="Pacific/Honolulu">Hawaii Time (HST)</option>
            <option value="Europe/London">London (GMT)</option>
            <option value="Europe/Paris">Paris (CET)</option>
            <option value="Europe/Berlin">Berlin (CET)</option>
            <option value="Asia/Tokyo">Tokyo (JST)</option>
            <option value="Asia/Shanghai">Shanghai (CST)</option>
            <option value="Asia/Dubai">Dubai (GST)</option>
            <option value="Australia/Sydney">Sydney (AEDT)</option>
            <option value="Pacific/Auckland">Auckland (NZDT)</option>
          </select>
        </div>

        {/* Summary */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Current Schedule Summary</h4>
          <div className="text-sm text-gray-700 space-y-1">
            <p><strong>Hours:</strong> {formatTo12Hour(workHours.start)} - {formatTo12Hour(workHours.end)}</p>
            <p><strong>Days:</strong> {workHours.work_days.length > 0 
              ? workHours.work_days.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ')
              : 'No days selected'
            }</p>
            <p><strong>Timezone:</strong> {workHours.timezone}</p>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 shadow-md"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save Work Hours'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkHoursSettings;
